import { DEVICE, OTHER_RMA_REASONS } from '@/shared/config';
import type { RmaOption, RmaOptionLists } from '../model/rma.types';

interface StoredOption {
  value: string;
  describe?: string | null;
}

const REASON_GROUP = {
  STANDARD: 'STANDARD',
  SPECIAL: 'SPECIAL',
};

/**
 * Selects the reason options offered for a device model. Bioflux and Biotres use their own list while every other or missing model uses the Biotres Pro list, and a list that has not been loaded yields an empty array.
 *
 * @param reasons - Reason options keyed by device model.
 * @param deviceModel - Stored device model the reasons are offered for.
 * @returns The reason options of the model in the global option order.
 */
const getModelReasonOptions = (
  reasons: RmaOptionLists['reasons'],
  deviceModel?: string,
): RmaOption[] => {
  const listModel =
    deviceModel === DEVICE.BIOFLUX || deviceModel === DEVICE.BIOTRES
      ? deviceModel
      : DEVICE.BIOTRES_PRO;
  return reasons[listModel] ?? [];
};

/**
 * Splits reason options into the combinable device issues and the exclusive other reasons the RMA form offers. Device issues are the STANDARD options, while other reasons are the SPECIAL options without closed account and returned without RMA, and without return to stock unless the facility has that program; PROGRAM options are never offered.
 *
 * @param options - Reason options of one device model.
 * @param hasReturnToStock - Whether the facility has the return to stock program.
 * @returns The device issue options and the other reason options.
 */
const splitReasonOptions = (options: RmaOption[], hasReturnToStock: boolean) => ({
  deviceIssues: options.filter((option) => option.group === REASON_GROUP.STANDARD),
  otherReasons: options.filter(
    (option) =>
      option.group === REASON_GROUP.SPECIAL &&
      option.value !== OTHER_RMA_REASONS.CLOSED_ACCOUNT &&
      option.value !== OTHER_RMA_REASONS.DEVICE_RETURNED_WITHOUT_RMA &&
      (option.value !== OTHER_RMA_REASONS.RETURN_TO_STOCK || hasReturnToStock),
  ),
});

/**
 * Resolves the display text of a stored option value. Looks the value up in the option list and falls back to the value itself when no option matches, as the API does.
 *
 * @param options - Option list to search.
 * @param value - Stored option value.
 * @returns The label of the matching option, or the value.
 */
const findOptionLabel = (options: Array<Pick<RmaOption, 'value' | 'label'>>, value: string) =>
  options.find((option) => option.value === value)?.label ?? value;

/**
 * Builds the form entry of an option row, carrying its display label. Keeps the label the row already has, such as one resolved by the API for an inactive option, and otherwise looks it up in the option list.
 *
 * @param options - Option list the label is looked up in.
 * @param option - Option row carrying its value, describe and possibly a label.
 * @returns The value, label and describe of the option.
 */
const toOptionEntry = (
  options: Array<Pick<RmaOption, 'value' | 'label'>>,
  { value, label, describe }: StoredOption & { label?: string },
) => ({ value, label: label ?? findOptionLabel(options, value), describe: describe ?? undefined });

/**
 * Reduces an option to the shape written to the API. Drops the label and any other read-only field, and turns a null describe into undefined so equal selections compare equal.
 *
 * @param option - Option carrying at least its value.
 * @returns The value and describe to send.
 */
const toOptionValueInput = ({ value, describe }: StoredOption) => ({
  value,
  describe: describe ?? undefined,
});

export {
  findOptionLabel,
  getModelReasonOptions,
  splitReasonOptions,
  toOptionEntry,
  toOptionValueInput,
};
